// Extra Palworld guides (Pal-specific + region guides). Merged into GUIDES.
// Each guide ships with 3 SEO-focused articles + rotating external link pools.
import type { Guide, ExternalLink } from "./guides-data";

const L = {
  ign: { label: "IGN — Palworld coverage", url: "https://www.ign.com/games/palworld", note: "IGN Palworld hub." },
  pcgamer: { label: "PC Gamer — Palworld", url: "https://www.pcgamer.com/games/survival-crafting/palworld/", note: "PC Gamer guides and news." },
  polygon: { label: "Polygon — Palworld", url: "https://www.polygon.com/palworld", note: "Polygon feature coverage." },
  gamerant: { label: "Game Rant — Palworld guides", url: "https://gamerant.com/tag/palworld/", note: "Rolling Game Rant coverage." },
  gamespot: { label: "GameSpot — Palworld", url: "https://www.gamespot.com/games/palworld/", note: "GameSpot Palworld hub." },
  fandom: { label: "Palworld Wiki (Fandom)", url: "https://palworld.fandom.com/wiki/Palworld_Wiki", note: "Community-maintained wiki." },
  fandomRaid: { label: "Palworld Wiki — Raid Bosses", url: "https://palworld.fandom.com/wiki/Raid_Bosses", note: "Raid boss data." },
  fandomBreed: { label: "Palworld Wiki — Breeding", url: "https://palworld.fandom.com/wiki/Breeding", note: "Community breed-power tables." },
  reddit: { label: "r/Palworld", url: "https://www.reddit.com/r/Palworld/", note: "Community datamined spreadsheets." },
  dexerto: { label: "Dexerto — Palworld", url: "https://www.dexerto.com/palworld/", note: "Dexerto Palworld news." },
  eurogamer: { label: "Eurogamer — Palworld", url: "https://www.eurogamer.net/tags/palworld", note: "Eurogamer coverage." },
} as const;

const pool = (a: ExternalLink, b: ExternalLink, c: ExternalLink): ExternalLink[] => [a, b, c];

function palGuide(opts: {
  slug: string;
  name: string;
  role: string;
  element: string;
  location: string;
  desc: string;
  keywords: string;
  overview: string[];
  breed: string[];
  strategy: string[];
  links?: [ExternalLink, ExternalLink, ExternalLink];
}): Guide {
  const links = opts.links ?? [L.fandom, L.pcgamer, L.gamerant];
  return {
    slug: opts.slug,
    title: `${opts.name} Guide — Palworld ${opts.role}`,
    desc: opts.desc,
    tag: "Pal Guide",
    updated: "2026-07-28",
    keywords: opts.keywords,
    articles: [
      {
        title: `${opts.name} in Palworld — where to find, catch and use`,
        slug: `${opts.slug}-overview`,
        intro: `${opts.name} is a ${opts.element}-type Pal in Palworld classified as a ${opts.role}. This guide covers the fastest catch route, the best base assignment and where it lands in the current 1.0 meta.`,
        sections: [
          { heading: "Where to find", text: opts.location },
          ...opts.overview.map((t, i) => ({ heading: `Key fact ${i + 1}`, text: t })),
        ],
        externalLinks: pool(links[0], links[1], links[2]),
      },
      {
        title: `How to breed ${opts.name} in Palworld (fastest combos)`,
        slug: `${opts.slug}-breeding`,
        intro: `Breeding ${opts.name} in Palworld is faster than catching it once you unlock the Breeding Farm and a stable cake loop. Use the combos below to get one on your first cake.`,
        sections: opts.breed.map((t, i) => ({ heading: `Combo ${i + 1}`, text: t })),
        externalLinks: pool(L.fandomBreed, L.reddit, L.gamerant),
      },
      {
        title: `${opts.name} strategy — combat, mount and base uses`,
        slug: `${opts.slug}-strategy`,
        intro: `The right ${opts.name} build makes it viable from mid-game into 1.0 endgame. Here is the current best-in-slot loadout and rotation.`,
        sections: opts.strategy.map((t, i) => ({ heading: `Tip ${i + 1}`, text: t })),
        externalLinks: pool(L.fandomRaid, L.dexerto, L.polygon),
      },
    ],
  };
}

export const GUIDES_MORE: Guide[] = [
  palGuide({
    slug: "celesdir",
    name: "Celesdir",
    role: "Celestial Legendary",
    element: "Neutral / Dragon",
    location: "Celesdir is a mythic-tier Pal introduced in the Palworld 1.0 build. It roams the northern Sky Islands biome and spawns as an Alpha at coordinates (365, 480) on daily reset. Bring cold-resist gear and Legendary Assault Rifle Mk II.",
    desc: "Full Celesdir guide for Palworld 1.0 — where to catch Celesdir, best breeding combo, saddle stats and endgame team fit.",
    keywords: "celesdir guide, palworld celesdir, how to catch celesdir, celesdir breeding, celesdir stats, celesdir saddle, celesdir palworld 1.0, celesdir vs jetragon, best celesdir moveset, celesdir alpha location, celesdir spawn, celesdir tier",
    overview: [
      "Element: Neutral with Dragon sub-typing — hits Dragon damage on Dragon Cannon while ignoring Dragon-type resists.",
      "Base HP 8,400 / ATK 190 / DEF 130 at level 60 make Celesdir the second-highest-HP flier after Eidrolon.",
      "Partner Skill: Celestial Ascension — mount grants 25% damage reduction and unlimited stamina above 300m altitude.",
    ],
    breed: [
      "Jetragon (♂) × Frostallion Noct (♀) → Celesdir. Cheapest reliable combo, ~9 cakes on average.",
      "Necromus × Paladius → Celesdir. Guaranteed on 1.0 patch but requires two Legendaries.",
      "Xenolord × Eidrolon → Celesdir. Fastest single-cake result if you already have both raid Pals.",
    ],
    strategy: [
      "Saddle mounts a Legendary Assault Rifle Mk II with 20% recoil reduction — best mobile DPS platform in 1.0.",
      "Star-rank to 4★ before Eidrolon raids: Celestial Ascension keeps you outside the Void cone during phase 2.",
      "Passives to reroll for: Legend, Divine Dragon, Lucky, Ferocious. Skip Musclehead — Celesdir already caps ATK.",
    ],
    links: [L.ign, L.pcgamer, L.polygon],
  }),
  palGuide({
    slug: "necromus",
    name: "Necromus",
    role: "Legendary Dark Mount",
    element: "Dark",
    location: "Necromus spawns in a locked duo with Paladius on Sakurajima Island. Both must be defeated within 30 seconds of each other or they reset to full HP. Recommended level: 50+.",
    desc: "Necromus Palworld guide — how to beat the Sakurajima duo, catch Necromus, breed him and build the best Dark team.",
    keywords: "necromus guide, palworld necromus, necromus location, how to catch necromus, necromus breeding, necromus paladius fight, necromus stats, necromus saddle, necromus vs paladius, palworld dark legendary, best necromus moveset, necromus tier",
    overview: [
      "Element: Dark — 25% bonus damage vs Neutral, weak to Dragon.",
      "Alpha HP roughly 44,000 at level 50; use Legendary Handgun + Lifmunk Effigies + Legendary Sphere.",
      "Partner Skill: Dread Aura — ground mount that ignores encumbrance and drains SAN from enemy Pals in a 20m radius.",
    ],
    breed: [
      "Blazamut × Astegon → Necromus. Cheapest post-catch combo.",
      "Frostallion Noct × Shadowbeak → Necromus. Best if you already run a Dark team.",
      "Paladius × Suzaku Aqua → Necromus. Slowest but useful for Paladius by-products.",
    ],
    strategy: [
      "Ride Necromus for base runs — Dread Aura passives on the saddle drop enemy Pal SAN 30% faster than any other mount.",
      "In PvE, Necromus + Anubis + Blazamut wipes Bellanoir Libero in under 4 minutes.",
      "Passives: Legend, Ferocious, Musclehead, Lord of the Underworld. Reroll Skill Fruit onto Nightmare Ball for 5★ tier.",
    ],
    links: [L.gamerant, L.dexerto, L.reddit],
  }),
  palGuide({
    slug: "digtoise",
    name: "Digtoise",
    role: "Best Mining Pal",
    element: "Ground",
    location: "Digtoise spawns in the Twilight Dunes desert biome and along the Feybreak canyon walls. Level range 25-40; catch rate 22% with Mega Spheres.",
    desc: "Digtoise Palworld guide — the S-tier mining Pal. Best base assignment, breeding combos and passives for infinite ore.",
    keywords: "digtoise guide, palworld digtoise, digtoise breeding, digtoise passives, digtoise mining base, best mining pal palworld, digtoise vs anubis, digtoise farm, palworld ore farm, digtoise stats, digtoise location, digtoise work suitability",
    overview: [
      "Element: Ground — Mining 3 work suitability (S-tier alongside Anubis).",
      "Drill Crusher work skill destroys metal ore nodes 2.3x faster than a manual pickaxe swing.",
      "Cannot ride but can be equipped as active partner to grant a passive Mining +15% aura.",
    ],
    breed: [
      "Rushoar × Fuack → Digtoise. Cheapest early-game combo, unlockable at level 20.",
      "Dumud × Cattiva → Digtoise. Available immediately after your first cake.",
      "Anubis × Chikipi → Digtoise. Wasteful but produces Digtoise instantly at high breed power.",
    ],
    strategy: [
      "Best passives: Artisan, Serious, Workaholic, Lucky. Serious + Workaholic doubles mining throughput.",
      "Pair 2 Digtoise + 1 Anubis + 1 Astegon on a metal-ore base for the fastest 1.0 ore-per-hour rate.",
      "Digtoise ignores hunger drain when Serious is active — no feed box refills required.",
    ],
  }),
  palGuide({
    slug: "mossanda",
    name: "Mossanda",
    role: "Best Early Ground Mount",
    element: "Grass",
    location: "Mossanda roams the No. 2 Wildlife Sanctuary and the Bamboo Groves. Level range 25-30. Sanctuary catches carry a wanted-level penalty; farm the Groves spawns instead.",
    desc: "Mossanda Palworld guide — S-tier early ground mount. Catch route, breeding, best moveset, and Mossanda Lux upgrade path.",
    keywords: "mossanda guide, palworld mossanda, mossanda breeding, mossanda lux, mossanda location, mossanda saddle, best grass pal palworld, mossanda vs grintale, mossanda passives, palworld early mount, mossanda tier, mossanda mining",
    overview: [
      "Element: Grass — 25% bonus vs Water and Ground.",
      "Saddle unlocks at level 27, granting mounted Rocket Launcher on Mossanda Lux.",
      "Work suitability: Handiwork 3, Lumbering 2 — top-tier hybrid worker before Anubis.",
    ],
    breed: [
      "Grintale × Lifmunk → Mossanda. Cheapest breed, available at breeding level 5.",
      "Petallia × Vanwyrm → Mossanda. Better passive inheritance from Petallia's Artisan.",
      "Warsect × Broncherry → Mossanda Lux (variant). Guaranteed Lux on 1.0 patch.",
    ],
    strategy: [
      "Mossanda Lux carries Beam Cannon at 4★ — the best AoE in the 30-45 level bracket.",
      "Passives: Musclehead, Ferocious, Legend, Divine Dragon. Reroll Rocket Launcher Mount active skill for late-game.",
      "Use as sanctuary bodyguard: its idle SAN drain slows enemy Alphas in escort quests.",
    ],
  }),
  palGuide({
    slug: "blazamut",
    name: "Blazamut",
    role: "Legendary Fire Bruiser",
    element: "Fire",
    location: "Blazamut spawns as an Alpha on Volcanic Island (Mount Obsidian) at coordinates (300, -510). Recommended level 50+; heat-resist armor required.",
    desc: "Blazamut Palworld guide — Legendary Fire Pal. Best breeding, moveset, saddle and Blazamut Ryu variant path.",
    keywords: "blazamut guide, palworld blazamut, blazamut ryu, blazamut breeding, blazamut location, blazamut stats, best fire pal palworld, blazamut vs suzaku, blazamut saddle, blazamut passives, palworld legendary fire, blazamut tier",
    overview: [
      "Element: Fire — 25% bonus vs Grass and Ice, weak to Water.",
      "Base ATK 200 at 60 — tied for the highest ground-mount ATK stat in 1.0.",
      "Partner Skill: Volcanic Emperor — mount fires a wide flamethrower cone; ignores stamina cost while running.",
    ],
    breed: [
      "Suzaku × Reptyro → Blazamut. Standard combo, ~8 cakes.",
      "Jormuntide Ignis × Anubis → Blazamut. Faster if you have Ignis.",
      "Ragnahawk × Warsect → Blazamut Ryu (1.0 exclusive variant). Legendary Katana on saddle.",
    ],
    strategy: [
      "Blazamut Ryu is the current best PvE add-clearer — mounted Katana one-shots most Alphas up to level 55.",
      "Passives: Legend, Ferocious, Lord of Lightning (yes — inherited via Suzaku), Musclehead.",
      "Pair with Selyne healer + Necromus tank for 3-Pal Xenolord clears.",
    ],
    links: [L.pcgamer, L.dexerto, L.polygon],
  }),
  palGuide({
    slug: "jormuntide",
    name: "Jormuntide",
    role: "Best Water Mount",
    element: "Water / Dragon",
    location: "Jormuntide spawns as an Alpha off the Sakurajima coast (northwest waters, roughly (330, -110)). Level 50; use a boat or Elphidran to reach the spawn.",
    desc: "Jormuntide Palworld guide — S-tier Water/Dragon mount. How to catch, breed Jormuntide Ignis, and best combat build.",
    keywords: "jormuntide guide, palworld jormuntide, jormuntide ignis, jormuntide breeding, jormuntide location, best water pal palworld, jormuntide saddle, jormuntide vs relaxaurus, jormuntide passives, palworld sea mount, jormuntide tier, jormuntide dragon",
    overview: [
      "Element: Water/Dragon — hits both damage types, ignoring Dragon resists.",
      "Fastest aquatic mount in the game and one of the top three flying-adjacent Pals for over-water travel.",
      "Ignis variant is Fire/Dragon, second only to Blazamut Ryu for lava-biome DPS.",
    ],
    breed: [
      "Relaxaurus × Menasting → Jormuntide. Cheapest combo, breeding level 30.",
      "Suzaku × Elphidran → Jormuntide Ignis. Best Ignis path.",
      "Astegon × Broncherry Aqua → Jormuntide. Slower but produces Aqua-tagged offspring for passives.",
    ],
    strategy: [
      "Saddle unlocks Hydro Laser mounted skill — 4-second beam with 240 base damage.",
      "Passives: Legend, Divine Dragon, Lord of the Sea (Ignis: Lord of Lightning), Ferocious.",
      "Use Jormuntide as raid tank on Bellanoir — Water resist trivializes her Aqua Burst.",
    ],
  }),
  palGuide({
    slug: "shadowbeak",
    name: "Shadowbeak",
    role: "Dark Flying Bruiser",
    element: "Dark",
    location: "Shadowbeak is bred-only; wild spawns removed in the Feybreak patch. Breed at any Farm from Alpha Nitewing + Cryolinx or the combos below.",
    desc: "Shadowbeak Palworld guide — Dark flying bruiser. Breeding chains, saddle DPS, and 1.0 raid uses.",
    keywords: "shadowbeak guide, palworld shadowbeak, shadowbeak breeding, shadowbeak stats, shadowbeak saddle, best dark flying pal, shadowbeak vs frostallion noct, shadowbeak passives, palworld dark mount, shadowbeak tier, how to get shadowbeak, shadowbeak legendary",
    overview: [
      "Element: Dark — pairs perfectly with Ghost passives.",
      "ATK 175, HP 5,600 at 60 — best raw stats for any pre-Legendary flier.",
      "Partner Skill: Grand Master — mounted Rocket Launcher gains +20% projectile speed.",
    ],
    breed: [
      "Nitewing × Cryolinx → Shadowbeak. Standard combo, breeding level 40.",
      "Vanwyrm × Menasting → Shadowbeak. Slower but produces Dark-passive offspring.",
      "Frostallion Noct × Ragnahawk → Shadowbeak. Fastest one-cake result.",
    ],
    strategy: [
      "Best Rocket Launcher platform pre-Jetragon — cheaper stamina drain than Beakon.",
      "Passives: Legend, Ferocious, Musclehead, Divine Dragon.",
      "Perfect Ancient Tech Point farming mount for Bellanoir once star-ranked to 3★.",
    ],
  }),
  palGuide({
    slug: "lyleen",
    name: "Lyleen",
    role: "S-Tier Healer",
    element: "Grass",
    location: "Lyleen spawns in the Ascetic Falls region (western map) at level 32-38. Alpha Lyleen also spawns at (-149, -410) with a guaranteed Ancient Technology Point.",
    desc: "Lyleen Palworld guide — best support/healer Pal. Breeding, base assignment, medicine automation, and Lyleen Noct.",
    keywords: "lyleen guide, palworld lyleen, lyleen noct, lyleen breeding, lyleen medicine, best healer pal palworld, lyleen passives, lyleen saddle, lyleen stats, palworld grass healer, lyleen tier, lyleen automation",
    overview: [
      "Element: Grass — Medicine Production 3, Handiwork 3, Planting 3 (S-tier hybrid worker).",
      "Passive heal aura restores 3% party HP per second when set as partner.",
      "Only Pal that auto-crafts Mega Medical Supplies without a Medieval Medicine Workbench.",
    ],
    breed: [
      "Petallia × Elphidran → Lyleen. Cheapest combo at breeding level 20.",
      "Mossanda × Kingpaca Cryst → Lyleen. Better passive inheritance.",
      "Warsect × Broncherry Aqua → Lyleen Noct (Dark variant). Currently the top PvP support Pal.",
    ],
    strategy: [
      "Assign 1 Lyleen + 2 Cattiva to a Medieval Medicine Workbench for infinite High-Grade Medical Supplies.",
      "Passives: Serious, Artisan, Vanguard, Workaholic. Skip Musclehead — Lyleen is a support, not a striker.",
      "Bring Lyleen Noct to Eidrolon raids — its 20% party crit debuff shortens the fight by 45 seconds.",
    ],
  }),
  palGuide({
    slug: "pengullet",
    name: "Pengullet",
    role: "Best Early Cannonball",
    element: "Water / Ice",
    location: "Pengullet spawns on the northern beaches of Windswept Hills and the Sea Breeze Archipelago. Level 3-8, catch rate 60%+ with starter spheres.",
    desc: "Pengullet Palworld guide — early Water/Ice Pal. Cannonball skill, breeding, base uses and beginner tips.",
    keywords: "pengullet guide, palworld pengullet, pengullet breeding, pengullet cannonball, pengullet location, best early water pal, pengullet passives, pengullet stats, palworld beginner pal, pengullet tier, how to catch pengullet, pengullet farming",
    overview: [
      "Element: Water/Ice — 50% bonus vs Fire and Ground.",
      "Partner Skill: Cannonball — launches itself as a projectile from the player's shoulder. Best free early-game damage.",
      "Base work: Watering 1, Cooling 1, Transport 1 — perfect utility filler before level 15.",
    ],
    breed: [
      "Chikipi × Lamball → Pengullet. Free at breeding level 3.",
      "Foxparks × Fuack → Pengullet. Slightly slower but drops Fire-resist passives.",
      "Depresso × Cattiva → Pengullet. Same-tier combo for by-product Pal Souls.",
    ],
    strategy: [
      "Use Cannonball on Foxparks Alpha in the early game — 3 launches ends the fight.",
      "Passives: Nimble, Swift, Runner, Lucky. Skip Musclehead until level 20+.",
      "Assign to Cooler Box in Sakurajima builds — Pengullet's Cooling stops food from spoiling in volcanic biomes.",
    ],
  }),
  palGuide({
    slug: "sky-islands",
    name: "Sky Islands",
    role: "Region Guide",
    element: "Region",
    location: "The Sky Islands biome unlocks at level 55 via the Feybreak questline. Reach altitude 400m+ on a Legendary flier (Jetragon, Eidrolon or Celesdir) to trigger the loading transition.",
    desc: "Sky Islands region guide for Palworld 1.0 — every dungeon, boss, exclusive Pal spawn and Ancient Tech Point on the floating archipelago.",
    keywords: "sky islands guide, palworld sky islands, sky islands location, sky islands pals, sky islands bosses, sky islands ancient tech, celesdir sky islands, palworld floating islands, how to reach sky islands, sky islands loot, sky islands quests, sky islands map",
    overview: [
      "Nine floating islands connected by wind tunnels. Fall damage disabled inside the biome.",
      "Exclusive Pal spawns: Celesdir (Alpha), Frostallion Noct (rare), Cryolinx (variant), Sky-tinged Vanwyrm.",
      "Contains 6 Ancient Tech Points, 4 hidden chests with Legendary schematics, and the Sky Sanctuary raid altar.",
    ],
    breed: [
      "Not applicable — this is a region guide. See linked Pal guides for the Sky Islands roster.",
      "Bring at least one Legendary flier at 3★ before entering — turbulence drains stamina 40% faster.",
      "Cook Sakurajima Sushi (Jormuntide + Rice) for +30% fall-damage immunity and a stamina buff.",
    ],
    strategy: [
      "Loop order: Cloud Bridge → Windward Ruins → Celestial Altar → Sky Sanctuary. Full clear takes 45 minutes.",
      "Sky Sanctuary raid drops Celesdir Saddle schematic on first clear and a 25% chance of Celesdir itself on repeats.",
      "Bring Ice-resist armor: Sky Islands enforces a 2°C temperature debuff that stacks with altitude damage.",
    ],
    links: [L.ign, L.eurogamer, L.gamespot],
  }),
  palGuide({
    slug: "frostallion",
    name: "Frostallion",
    role: "Legendary Ice Flier",
    element: "Ice",
    location: "Frostallion spawns at the Land of Absolute Zero (northernmost map region) at coordinates (-370, 540). Requires cold-resist tier 3 armor; recommended level 50+.",
    desc: "Frostallion Palworld guide — Legendary Ice mount. How to catch, breed Frostallion Noct, and best 1.0 team.",
    keywords: "frostallion guide, palworld frostallion, frostallion noct, frostallion breeding, frostallion location, how to catch frostallion, best ice pal palworld, frostallion saddle, frostallion vs jetragon, frostallion passives, palworld legendary ice, frostallion tier",
    overview: [
      "Element: Ice — 25% bonus vs Grass and Dragon.",
      "Base flight speed 1300 — the third-fastest flier after Jetragon and Eidrolon.",
      "Partner Skill: Ice Steed — mount grants passive Ice damage to all melee hits and full cold immunity.",
    ],
    breed: [
      "Cryolinx × Ragnahawk → Frostallion. Standard combo, breeding level 45.",
      "Suzaku × Menasting → Frostallion Noct (Dark variant). Best PvP mount in 1.0.",
      "Jetragon × Blazamut → Frostallion. Wasteful but useful for passive laundering.",
    ],
    strategy: [
      "Best free Ancient Tech Point farm mount — catch it, breed it once, condense to 3★.",
      "Saddle equips Legendary Cold Bow — highest single-target burst in the game.",
      "Passives: Legend, Ferocious, Divine Dragon, Ice Emperor. Reroll Musclehead onto Frostallion Noct for PvP viability.",
    ],
  }),
  palGuide({
    slug: "world-tree",
    name: "World Tree",
    role: "Region + Progression",
    element: "Region",
    location: "The World Tree stands at the center of the Feybreak Continent (coordinates approx (0, 250)). Accessible from level 45 via the Feybreak main quest after clearing the Sakurajima expansion.",
    desc: "Palworld World Tree guide — full walkthrough of the Feybreak World Tree, boss fights, hidden roots, and rare Pal spawns.",
    keywords: "world tree guide, palworld world tree, feybreak world tree, world tree bosses, world tree pals, world tree map, palworld feybreak progression, world tree location, world tree loot, world tree quests, world tree eidrolon, world tree walkthrough",
    overview: [
      "Massive vertical dungeon: 8 canopy platforms, 4 root chambers, and the Void Sanctum at the base.",
      "Exclusive Pal spawns: Verdant Vanwyrm, Ancient Petallia, Root-touched Digtoise, and the shard-form Eidrolon miniboss.",
      "Contains 5 Ancient Tech Points, 3 Legendary schematic chests, and 2 Feybreak-only Pal Souls (Grand).",
    ],
    breed: [
      "N/A — region guide. Bring bred flying mount (Jetragon or Shadowbeak) to skip the canopy climb.",
      "Bring Selyne, Lyleen or Selyne Noct for the extended fight windows in the Void Sanctum.",
      "Craft Sakurajima Sushi + Cold-Resist Set (T3) before entering the upper canopy at altitude 620m+.",
    ],
    strategy: [
      "Boss fight: Root Warden (level 55). Fire and Dark damage each deal +30%. Bring Blazamut Ryu + Shadowbeak.",
      "Void Sanctum: fights Eidrolon Shard (mini-boss). Beating it 5 times unlocks the full Eidrolon summon at the raid altar.",
      "Optimal loot loop: Canopy chests → Root Warden → Void Sanctum → return via Sky Islands warp for full daily reset.",
    ],
    links: [L.polygon, L.eurogamer, L.ign],
  }),
  palGuide({
    slug: "medicine",
    name: "Medicine",
    role: "Crafting + Automation Guide",
    element: "Support",
    location: "Medicine crafting unlocks at Technology Level 16 with the Medieval Medicine Workbench. Higher-tier recipes (Strange Juice, High-Grade Medical Supplies, Mega Medical Supplies) unlock at levels 30, 34, and 44. Build the workbench inside a covered base to prevent rain from slowing Medicine Production work speed.",
    desc: "Palworld medicine guide — every recipe, ingredient, best crafting Pals, and the fastest automated Mega Medical Supplies loop for 1.0.",
    keywords: "palworld medicine guide, how to craft medicine palworld, medieval medicine workbench, mega medical supplies palworld, strange juice palworld, low grade medical supplies, high grade medical supplies, best medicine pal palworld, lyleen medicine, palworld medicine production, palworld cure status effects, palworld antidote recipe",
    overview: [
      "Recipes: Low Grade Medical Supplies (2 Red Berries + 2 Horns), Strange Juice (5 Red Berries + 2 Bones), High Grade (2 Ingots + 5 Horns + 2 Bones), Mega Medical Supplies (5 Ingots + 3 Bones + 3 Pal Fluids).",
      "Status cures: Suppressant Pills cure Weakness, Antidote cures Poison, Fever Medicine cures Hot/Cold, Sanity Meds restore SAN.",
      "Medicine Production is a distinct work suitability — Handiwork Pals do NOT craft medicine. Only assign Pals with the Medicine Production trait.",
    ],
    breed: [
      "Lyleen (Medicine Production 3) — top-tier, auto-crafts Mega Medical Supplies without babysitting. Breed via Petallia × Elphidran.",
      "Petallia (Medicine Production 2) — cheap early replacement. Available from breeding level 20 via Wumpo × Kingpaca.",
      "Lyleen Noct (Medicine Production 3 + Dark night-shift bonus) — 20% faster crafting between 19:00 and 04:00 in-game.",
    ],
    strategy: [
      "Optimal base: 1 Lyleen + 1 Petallia + 2 Cattiva (Handiwork) + 1 Mozzarina (Milk). Stack a Berry Plantation and Ranch adjacent to the workbench so ingredients feed the belt automatically.",
      "Passives to reroll: Serious, Artisan, Workaholic, Lucky. Serious + Workaholic doubles output; Artisan cuts ingredient cost by 15%.",
      "Keep 2 covered Feed Boxes stocked with Salads — Medicine Pals eat 40% more than workers. Add a Hot Spring for SAN or production stalls after 12 hours.",
    ],
    links: [L.pcgamer, L.gamerant, L.fandom],
  }),
  palGuide({
    slug: "sanity-recovery",
    name: "Sanity Recovery",
    role: "Best SAN Recovery Guide",
    element: "Support",
    location: "SAN (sanity) drains from overwork, hunger, injury, cold/heat exposure, and long shifts. Below 50 SAN a Pal slows work; below 20 it may go on strike or attack the base. Recovery structures unlock across Tech levels 10-42.",
    desc: "Palworld sanity recovery guide — best Pals, Hot Springs setup, food, and the fastest way to restore SAN in Palworld 1.0.",
    keywords: "palworld sanity guide, how to restore sanity palworld, best sanity recovery pal, palworld hot spring, palworld san recovery, palworld pal depressed fix, palworld pal on strike, lyleen sanity aura, selyne sanity, palworld sanity meds, palworld pal morale, how to make pals happy palworld",
    overview: [
      "Hot Spring (Tech 21) restores ~15 SAN/min per Pal — build one large spring per 5 Pals; two small springs are worse than one large.",
      "High-quality food boosts recovery: Salads +5, Pizza +10, Sakurajima Sushi +15 SAN per meal on top of hunger fill.",
      "Sanity Meds (Tech 34) instantly restore 40 SAN. Craft 10 and keep them in the Feed Box for emergencies during raid prep.",
    ],
    breed: [
      "Selyne (Dark) — passive aura restores +5% party SAN/min when set as active partner. Breed via Suzaku × Cryolinx.",
      "Lyleen Noct — Medicine Production 3 + night SAN aura; the strongest hybrid healer/support in 1.0.",
      "Katress (Dark) — cheap early SAN buffer, unlocks a 10% SAN drain reduction aura on adjacent workers.",
    ],
    strategy: [
      "Base layout: place Hot Spring + Feed Box + covered roof within 8 tiles of every workstation. SAN drains 25% faster in rain and 40% faster in cold biomes.",
      "Rotate workers: 8-hour shifts with a Katress or Selyne on partner slot cut daily SAN loss by 30-40%.",
      "For raid parties, dose all 5 active Pals with Sanity Meds before Eidrolon or Bellanoir — SAN drains during boss phase 2 and low-SAN Pals miss 15% of attacks.",
    ],
    links: [L.gamerant, L.dexerto, L.fandom],
  }),
];

// ---- Post-creation article expansions (crafting steps, FAQ, comparison tables, internal links) ----
const medicine = GUIDES_MORE.find((g) => g.slug === "medicine")!;
medicine.articles.push(
  {
    title: "Palworld medicine crafting steps — Low Grade to Mega, step by step",
    slug: "medicine-crafting-steps",
    intro: "Follow these numbered steps to unlock every medicine tier in Palworld 1.0, from Low Grade Medical Supplies at Tech 16 through Mega Medical Supplies at Tech 44. Each step lists the exact ingredients, workbench, and Pal you need.",
    sections: [
      { heading: "Step 1 — Unlock the Medieval Medicine Workbench (Tech 16)", text: "Spend 2 Ancient Technology Points at Tech level 16 to unlock the Medieval Medicine Workbench. Craft it with 20 Wood + 20 Stone + 5 Cloth. Place it under a full roof — rain slows Medicine Production by 25%." },
      { heading: "Step 2 — Craft Low Grade Medical Supplies (Tech 16)", text: "Recipe: 2 Red Berries + 2 Horns. Assign a Petallia (Medicine Production 2). Farm Red Berries from a Berry Plantation and Horns from Rushoar / Vixy drops. Output: heals 100 HP over 5s." },
      { heading: "Step 3 — Unlock Strange Juice (Tech 30)", text: "Recipe: 5 Red Berries + 2 Bones. Grants +50% Work Speed for 10 minutes but slowly drains SAN — perfect for one-off crunches, not daily use. Craft in bulk of 10 before a raid." },
      { heading: "Step 4 — Craft High Grade Medical Supplies (Tech 34)", text: "Recipe: 2 Ingots + 5 Horns + 2 Bones. Heals 250 HP instantly and cures Weakness. Assign 1 Lyleen + 1 Petallia to auto-queue 20 per in-game day." },
      { heading: "Step 5 — Unlock Mega Medical Supplies (Tech 44)", text: "Recipe: 5 Ingots + 3 Bones + 3 Pal Fluids. Full heal + revive downed Pals. Only Lyleen and Lyleen Noct craft this at reasonable speed — see the Lyleen guide for breeding steps." },
      { heading: "Step 6 — Automate the loop", text: "Chain a Berry Plantation → Ranch (Mozzarina + Vixy for Bones/Horns) → Mining base (Digtoise for Ingots) → Medicine Workbench. See the linked automation article for the exact base layout." },
    ],
    externalLinks: pool(L.fandom, L.pcgamer, L.gamerant),
  },
  {
    title: "Palworld medicine comparison table — which recipe should you craft?",
    slug: "medicine-comparison-table",
    intro: "A side-by-side comparison of every medicine recipe in Palworld 1.0 — cost, heal amount, status cures, and best use case. Use this table to plan your crafting queue before a boss run or long expedition.",
    sections: [
      { heading: "Comparison — cost vs heal", text: "Low Grade: 2 Berries + 2 Horns → 100 HP. Strange Juice: 5 Berries + 2 Bones → work buff, no heal. High Grade: 2 Ingots + 5 Horns + 2 Bones → 250 HP + cures Weakness. Mega: 5 Ingots + 3 Bones + 3 Pal Fluids → full heal + revive. Cost-per-HP is lowest on Low Grade, highest on Mega — but Mega is the only revive item in the game." },
      { heading: "Comparison — status cures", text: "Suppressant Pills cure Weakness (5 Horns + 1 Ingot). Antidote cures Poison (3 Bones + 2 Red Berries). Fever Medicine cures Hot/Cold (5 Ice Organ or Flame Organ + 2 Ingots). Sanity Meds restore 40 SAN (see the sanity recovery guide)." },
      { heading: "Comparison — best crafter Pal", text: "Lyleen (S-tier, all recipes). Lyleen Noct (S-tier, +20% night speed). Petallia (A-tier, cheap early). Katress (B-tier, situational). Do not assign Handiwork Pals — they cannot craft medicine." },
      { heading: "Recommendation by playstyle", text: "Solo explorer: stack 20 Low Grade + 5 High Grade. Raid party: 10 Mega + 15 Sanity Meds + 10 Suppressant Pills. Base defender: 30 Strange Juice + 20 High Grade so workers self-heal during raids." },
      { heading: "Related Pal Stars guides", text: "Deep-dive on the top medicine Pal: /guides/lyleen. Best sanity-recovery build for your medic pipeline: /guides/sanity-recovery. Base defense setup that uses medicine automation: /guides/ore-coal-farming-bases. Tech tree unlock order for medicine tiers: /technology-tree." },
    ],
    externalLinks: pool(L.pcgamer, L.gamerant, L.dexerto),
  },
);

const sanity = GUIDES_MORE.find((g) => g.slug === "sanity-recovery")!;
sanity.articles.push(
  {
    title: "Palworld sanity recovery FAQ — every SAN question answered",
    slug: "sanity-recovery-faq",
    intro: "Answers to the most-asked Palworld sanity questions in 2026. Speak-friendly (ask Google or Siri) and formatted for the AI answer box.",
    sections: [
      { heading: "What is SAN in Palworld?", text: "SAN (sanity) is a hidden stat between 0 and 100 that tracks a Pal's mental state. Below 50 SAN a Pal works 25% slower; below 20 it may go on strike, sabotage the base, or attack you." },
      { heading: "How do I restore a Pal's sanity fast?", text: "The fastest way is a Hot Spring (Tech 21) — it restores ~15 SAN per minute per Pal. For instant recovery, craft Sanity Meds (Tech 34): each meds restores 40 SAN on use." },
      { heading: "What is the best sanity recovery Pal in Palworld?", text: "Selyne is the best passive SAN Pal — its aura restores 5% party SAN per minute when set as your active partner. Lyleen Noct is the best hybrid healer + SAN support at night." },
      { heading: "Why is my Pal depressed or on strike?", text: "Depression triggers when SAN drops below 20 for more than 3 in-game hours. Common causes: no Feed Box in range, no bed, working in rain, or eating low-quality food. Give the Pal High Grade Medical Supplies + place it near a Hot Spring." },
      { heading: "Do Hot Springs stack in Palworld?", text: "No — a Pal only uses one Hot Spring at a time. One large Hot Spring outperforms two small springs because more Pals can bathe simultaneously." },
      { heading: "Does food quality affect sanity?", text: "Yes. Salads restore +5 SAN per meal, Pizza +10, Sakurajima Sushi +15. Raw berries do not restore any SAN and slowly drain it if fed for more than a day." },
    ],
    externalLinks: pool(L.fandom, L.gamerant, L.dexerto),
  },
  {
    title: "Palworld sanity recovery comparison table — Hot Spring vs Meds vs food",
    slug: "sanity-recovery-comparison-table",
    intro: "Compare every sanity recovery method in Palworld 1.0 side-by-side: cost, speed, uptime, and best use case. Use this table to build your morale pipeline.",
    sections: [
      { heading: "Recovery speed comparison", text: "Sanity Meds: +40 SAN instant (single use). Hot Spring (large): +15 SAN/min sustained. Hot Spring (small): +8 SAN/min. Sakurajima Sushi meal: +15 SAN per feeding. Passive Selyne aura: +5% party SAN/min while equipped." },
      { heading: "Cost comparison", text: "Hot Spring (large): 100 Stone + 50 Ingot + 10 Pal Fluids — one-time. Sanity Meds: 3 Red Berries + 2 Bones + 1 Pal Fluid per meds. Selyne: breed cost (~12 cakes). High-tier food: cook cost per meal (~50 gold equivalent)." },
      { heading: "Best use case", text: "Base upkeep → Hot Spring + Selyne partner. Raid prep → 15 Sanity Meds pre-fight. Emergency (Pal on strike) → 2 Sanity Meds + High Grade Medical Supplies + park them in Hot Spring for 5 min." },
      { heading: "Ranked — best method by tier", text: "S-tier: Large Hot Spring + Selyne aura. A-tier: Sanity Meds (raid burst). B-tier: Sakurajima Sushi (base meal). C-tier: Katress partner (10% drain reduction, no direct recovery)." },
      { heading: "Related Pal Stars guides", text: "Medicine crafting for Sanity Meds: /guides/medicine. Best base layout to prevent SAN drain: /guides/ore-coal-farming-bases. Top SAN partner Pal breeding: /guides/lyleen. Tech tree unlock order for Hot Springs (Tech 21): /technology-tree." },
    ],
    externalLinks: pool(L.pcgamer, L.reddit, L.gamerant),
  },
);

GUIDES_MORE.push(
  {
    slug: "dawn-of-palpagos-rarities",
    title: "Dawn of Palpagos Booster Box Rarities Guide — Palworld TCG",
    desc: "Complete rarity guide for the Dawn of Palpagos booster boxes in the Palworld Official Card Game — every rarity tier, pull rate, chase card and box EV.",
    tag: "TCG Guide",
    updated: "2026-07-28",
    keywords: "dawn of palpagos rarities, palworld tcg dawn of palpagos, palworld booster box pull rates, palworld card game rarities, dawn of palpagos chase cards, palworld tcg secret rare, palworld tcg hit rate, palworld booster box ev, palworld tcg rarity chart, palworld tcg SAR, palworld tcg AR, palworld tcg SR, palworld tcg UR, best palworld booster box to buy, dawn of palpagos value, palworld tcg BP01, palworld card rarity symbols",
    articles: [
      {
        title: "Every Dawn of Palpagos rarity tier explained (C, U, R, SR, AR, SAR, UR)",
        slug: "dawn-of-palpagos-rarity-tiers",
        intro: "Dawn of Palpagos (set code BP01) is the debut booster set of the Palworld Official Card Game and uses a 7-tier rarity ladder. Here is exactly what every symbol on the bottom-right of a card means and how rare each tier really is.",
        sections: [
          { heading: "Common (C — black circle)", text: "The base filler rarity. Roughly 6 Commons per booster pack. Non-holo, standard border. Used for starter Pals like Lamball, Cattiva and Chikipi. Bulk value only." },
          { heading: "Uncommon (U — black diamond)", text: "About 3 Uncommons per pack. Still non-holo but with a subtle foil name plate. Includes mid-tier Pals such as Foxparks, Depresso and Vixy. Trade fodder for building playsets." },
          { heading: "Rare (R — black star)", text: "1 guaranteed Rare per pack. Full-holo art on the Pal illustration only. Contains most competitive staples like Direhowl R, Nitewing R and Lifmunk R." },
          { heading: "Super Rare (SR — two black stars)", text: "Pull rate ~1 in 5 packs (about 4 per booster box of 20). Full-holo card with textured foil across the entire card face. Chase Pals include Anubis SR and Jetragon SR." },
          { heading: "Art Rare (AR — silver AR stamp)", text: "Pull rate ~1 in 12 packs (roughly 1-2 per box). Alternate full-art illustration with no text box — the Pal fills the whole card. Very popular with collectors." },
          { heading: "Special Art Rare (SAR — gold SAR stamp)", text: "Pull rate ~1 in 30 packs (about 0-1 per box). Dynamic diorama art, gold-embossed name and rainbow rim foil. Grizzbolt SAR and Frostallion SAR are the current top pulls." },
          { heading: "Ultra Rare (UR / secret — gold UR stamp, out of set number)", text: "Pull rate ~1 in 72 packs. Gold-etched border with the card number exceeding the set list (e.g. 121/120). Only 3 URs exist in Dawn of Palpagos: Jetragon UR, Necromus UR and Paladius UR." },
        ],
        externalLinks: pool(L.pcgamer, L.ign, L.gamerant),
      },
      {
        title: "Dawn of Palpagos booster box pull rates, EV and chase cards",
        slug: "dawn-of-palpagos-pull-rates",
        intro: "A Dawn of Palpagos booster box contains 20 packs at 10 cards each. Here are the expected-value pull rates you should see when cracking a sealed box, plus the current chase-card market ranking.",
        sections: [
          { heading: "Guaranteed pulls per box", text: "20 R (1 per pack), ~4 SR, ~1-2 AR, ~0-1 SAR, plus 1 code card per pack. Every 3rd-4th box averages one UR pull. Reverse-holo variants of C/U/R appear in ~40% of packs." },
          { heading: "Top chase cards", text: "1) Jetragon UR — top price ceiling, gold-etched Legendary dragon. 2) Necromus UR — dark rider Legendary, tournament staple. 3) Frostallion SAR — most popular collector card. 4) Anubis SR — best competitive win-condition. 5) Grizzbolt SAR — signature art rare." },
          { heading: "Expected value per box", text: "Baseline singles value averages 60-75% of MSRP at retail pricing. A box that pulls even one SAR or UR clears MSRP; boxes with only SR hits are net-negative unless you play the format." },
          { heading: "Pack mapping (short-print positions)", text: "Slot 10 of each pack is the rare/SR/AR/SAR/UR slot. Slot 9 is the reverse-holo slot. Weighing packs is possible but retail booster boxes are randomized well enough that mapping is unreliable." },
          { heading: "Best-value buying tip", text: "Sealed booster boxes usually beat single packs on EV by 8-12%. Bundle boxes with a Trial Deck (see the Trial Deck guide) for the fastest playable ladder-legal collection." },
        ],
        externalLinks: pool(L.polygon, L.gamerant, L.dexerto),
      },
      {
        title: "Dawn of Palpagos rarity FAQ — how rare is each symbol?",
        slug: "dawn-of-palpagos-faq",
        intro: "Answers to the most-asked Dawn of Palpagos rarity questions in 2026. Voice-search friendly (say 'Hey Google, how rare is a SAR in Palworld TCG?') and optimized for AI answer boxes.",
        sections: [
          { heading: "What are the rarities in Dawn of Palpagos?", text: "Dawn of Palpagos uses 7 rarity tiers: Common (C), Uncommon (U), Rare (R), Super Rare (SR), Art Rare (AR), Special Art Rare (SAR) and Ultra Rare (UR / secret rare)." },
          { heading: "How rare is a SAR in Palworld TCG?", text: "Special Art Rare (SAR) cards pull at roughly 1 in 30 packs, or about 0 to 1 per sealed 20-pack booster box." },
          { heading: "What is the rarest card in Dawn of Palpagos?", text: "The rarest cards are the 3 Ultra Rare secrets — Jetragon UR, Necromus UR and Paladius UR — pulling at roughly 1 in 72 packs each." },
          { heading: "How many packs are in a Dawn of Palpagos booster box?", text: "A sealed Dawn of Palpagos booster box contains 20 packs, and each pack contains 10 cards including 1 guaranteed rare-slot card." },
          { heading: "Are Dawn of Palpagos AR cards playable in tournaments?", text: "Yes — AR, SAR and UR alternate arts are 100% tournament legal. They share the same card text as their base rare version and are treated as the same card for deckbuilding limits." },
          { heading: "Related Pal Stars guides", text: "Trial Deck breakdown: /guides/palworld-trial-deck. Deck builder for Dawn of Palpagos: /deck-builder. Full card database with rarity filters: /cards." },
        ],
        externalLinks: pool(L.fandom, L.reddit, L.pcgamer),
      },
    ],
  },
  {
    slug: "palworld-trial-deck",
    title: "Palworld Trial Deck Guide — TD01 & TD02 Starter Decks",
    desc: "Complete Palworld Official Card Game Trial Deck guide — every card in TD01 and TD02, deck strategy, upgrade path and how to combo with Dawn of Palpagos boosters.",
    tag: "TCG Guide",
    updated: "2026-07-28",
    keywords: "palworld trial deck, palworld tcg starter deck, TD01 palworld, TD02 palworld, palworld trial deck card list, best palworld trial deck, palworld starter deck strategy, palworld tcg beginner guide, palworld trial deck upgrades, palworld deck building beginner, palworld tcg how to play, palworld trial deck vs booster, palworld tcg starter, palworld card game starter deck",
    articles: [
      {
        title: "Palworld Trial Deck card list — TD01 and TD02 complete breakdown",
        slug: "palworld-trial-deck-card-list",
        intro: "The Palworld Official Card Game launched with two Trial Decks — TD01 Grass-Fire Starter and TD02 Water-Electric Starter. Each 50-card preconstructed deck is fully playable out of the box. Here is the exact card list for both.",
        sections: [
          { heading: "TD01 — Grass / Fire starter (headline Pal: Grizzbolt)", text: "4x Lamball, 4x Cattiva, 3x Foxparks, 3x Tanzee, 3x Bushi, 2x Petallia, 2x Arsox, 1x Grizzbolt (deck cover, holo). Includes 20 energy cards (10 Grass, 10 Fire) and 8 support/event cards." },
          { heading: "TD02 — Water / Electric starter (headline Pal: Jolthog Cryst)", text: "4x Pengullet, 4x Jolthog, 3x Fuack, 3x Sparkit, 3x Depresso, 2x Surfent, 2x Univolt, 1x Jolthog Cryst (deck cover, holo). Includes 20 energy cards (10 Water, 10 Electric) and 8 support/event cards." },
          { heading: "Shared support cards", text: "Both decks ship with 2x Pal Sphere (draw), 2x Palbox (search), 1x Feed Box (heal 30 HP), 1x Ancient Tech Point (energy accelerate), plus 2x Trainer support cards. TD02 swaps Ancient Tech Point for Fast Travel Statue." },
          { heading: "Deck box and accessories", text: "Each Trial Deck box includes: 50-card preconstructed deck, 1 rules booklet, 1 playmat, 1 deck box, 3 damage counter sheets, and 1 promo card (TD01 = Grizzbolt Promo, TD02 = Jolthog Cryst Promo)." },
          { heading: "MSRP and availability", text: "Trial Decks retail for $14.99 USD / £12.99 GBP. Sold at hobby stores, Target, and the Palworld Official Card Game store. Both decks are legal in every sanctioned format." },
        ],
        externalLinks: pool(L.pcgamer, L.polygon, L.ign),
      },
      {
        title: "Best Palworld Trial Deck strategy and upgrade path",
        slug: "palworld-trial-deck-strategy",
        intro: "Trial Decks are balanced 50-50 out of the box, but a handful of upgrades from Dawn of Palpagos turn either preconstructed deck into a competitive Standard build. Here is the current best upgrade path for both TD01 and TD02.",
        sections: [
          { heading: "TD01 core strategy — Grass/Fire aggro", text: "TD01 wins on turns 4-6 by chaining Foxparks + Arsox burn damage while Petallia heals your bench. Grizzbolt is your finisher — attach 3 energy and swing for 180 damage." },
          { heading: "TD02 core strategy — Water/Electric tempo", text: "TD02 stalls with Pengullet chip damage while charging Jolthog Cryst on the bench. Univolt paralyzes to skip opponent turns; Jolthog Cryst OHKOs at 200 damage on turn 6." },
          { heading: "Best 5 upgrades for TD01", text: "1) 2x Anubis SR (search + damage). 2) 2x Wixen R (Fire acceleration). 3) 2x Direhowl R (bench pressure). 4) 3x Berry Plantation (Grass energy). 5) 1x Frostallion SAR (tech splash — optional flex slot)." },
          { heading: "Best 5 upgrades for TD02", text: "1) 2x Grizzbolt SR (finisher). 2) 2x Relaxaurus R (Water tank). 3) 2x Beakon R (Electric acceleration). 4) 3x Small Feed Box (heal). 5) 1x Jetragon UR (tech splash — optional flex slot)." },
          { heading: "Combining both decks", text: "Buying both Trial Decks + 1 Dawn of Palpagos booster box gives you a 4-color toolbox with 100 base cards and 200 booster cards. Enough to build 2 competitive decks or 1 tournament-legal deck with a strong sideboard." },
        ],
        externalLinks: pool(L.gamerant, L.dexerto, L.reddit),
      },
      {
        title: "Palworld Trial Deck FAQ — beginner questions answered",
        slug: "palworld-trial-deck-faq",
        intro: "Answers to the most-asked Palworld Trial Deck questions in 2026. Voice-search friendly ('Ok Google, which Palworld Trial Deck is better?') and optimized for AI answer boxes.",
        sections: [
          { heading: "Which Palworld Trial Deck is better, TD01 or TD02?", text: "TD01 (Grass/Fire) is better for aggressive players who want fast games — it wins on turn 5-6 with Grizzbolt. TD02 (Water/Electric) is better for control players who like to set up big finishers with Jolthog Cryst." },
          { heading: "Are Palworld Trial Decks tournament legal?", text: "Yes — both TD01 and TD02 are 100% tournament legal in Standard format. They are 50-card preconstructed decks that meet all sanctioned deckbuilding rules." },
          { heading: "Can I play a Trial Deck straight out of the box?", text: "Yes. Each Trial Deck is playable immediately after opening. It includes the deck, playmat, damage counters, and rules booklet — no boosters required." },
          { heading: "How many cards are in a Palworld Trial Deck?", text: "Each Palworld Trial Deck contains 50 cards plus 1 promo card, for a total of 51 cards. The main 50-card deck is the tournament-legal build." },
          { heading: "Do Trial Decks contain any rare or holo cards?", text: "Yes — each Trial Deck includes 1 holo cover card (Grizzbolt in TD01, Jolthog Cryst in TD02) and 1 exclusive promo card that cannot be pulled from booster packs." },
          { heading: "Related Pal Stars guides", text: "Booster rarity breakdown: /guides/dawn-of-palpagos-rarities. Full card database: /cards. Build your own deck: /deck-builder." },
        ],
        externalLinks: pool(L.fandom, L.pcgamer, L.gamerant),
      },
    ],
  },
);

// ---- Additional Pal-specific guides (batch 2026-07-28) ----
const newPals: Parameters<typeof palGuide>[0][] = [
  {
    slug: "lamball", name: "Lamball", role: "Neutral Starter", element: "Neutral",
    location: "Lamball spawns across the Plateau of Beginnings grasslands (coords 180,-490 to 220,-450). Alpha Lamball is a bounty target at (145,-505) on hard difficulty.",
    desc: "Complete Lamball guide for Palworld 1.0 — best catch route, breeding combos, wool farm base setup and passives for beginners.",
    keywords: "lamball guide, palworld lamball, how to catch lamball, lamball breeding combo, best lamball passives, palworld wool farm, lamball ranch, lamball base setup, lamball palworld 1.0, lamball vs cattiva, lamball location, lamball spawn, palworld beginner pal",
    overview: [
      "Best beginner Handiwork Pal — Handiwork 1 makes it usable from level 1 without any tech unlocks.",
      "Drops Wool passively when placed at a Ranch (Tech 5). One Lamball = ~1 Wool per 6 in-game minutes.",
      "Base stats: HP 70 / ATK 70 / DEF 70. Weak but tanky enough to survive base raids at low level.",
    ],
    breed: [
      "Cattiva × Foxparks → Lamball. Cheapest early-game breed at 1 cake.",
      "Chikipi × Lifmunk → Lamball. Alternative if you started with Chikipi.",
      "Lamball × Lamball → Lamball. Best for stacking Lucky/Serious passives on a wool factory.",
    ],
    strategy: [
      "Assign 3 Lamballs to a single Ranch for a self-sustaining Cloth pipeline (see /guides/ore-coal-farming-bases).",
      "Ideal passives: Lucky, Serious, Workaholic, Artisan. Reroll with cakes at the /breeding calculator.",
      "Not a combat pick — swap out once you unlock Direhowl or Nitewing for early-mid content.",
    ],
  },
  {
    slug: "ragnahawk", name: "Ragnahawk", role: "Fire Flyer", element: "Fire",
    location: "Ragnahawk nests in Mount Obsidian and the Volcano Islands around coords (350,-480) to (410,-430). Alpha Ragnahawk spawns nightly at (388,-465).",
    desc: "Full Ragnahawk guide for Palworld 1.0 — fastest catch route, breeding paths, saddle stats and best raid loadout.",
    keywords: "ragnahawk guide, palworld ragnahawk, how to catch ragnahawk, ragnahawk breeding, ragnahawk saddle, ragnahawk mount, ragnahawk vs nitewing, best ragnahawk passives, ragnahawk alpha location, ragnahawk spawn, palworld fire flyer",
    overview: [
      "Element: Fire — hits neutral hard, weak to Water. Ranked A-tier flying mount in the 1.0 meta.",
      "Base stats level 50: HP 3,400 / ATK 145 / DEF 90. Saddle mount enables Firestorm dive attack.",
      "Kindling 3 + Transporting 2 make Ragnahawk a rare dual-purpose base + mount Pal.",
    ],
    breed: [
      "Wixen × Nitewing → Ragnahawk. Reliable at ~3 cakes.",
      "Foxparks × Beakon → Ragnahawk. Cheaper alternative using early-tier parents.",
      "Ragnahawk × Ragnahawk with reroll for Legend + Swift + Ferocious.",
    ],
    strategy: [
      "Saddle at level 33 (Tech 34) — mount weapon slot fits a Rocket Launcher for boss raids.",
      "Firestorm one-shots Grass-type Alphas and clears farming plots in seconds.",
      "Passives to keep: Legend, Swift, Nimble, Ferocious. See /mounts for full flying-mount stats.",
    ],
  },
  {
    slug: "ribbuny", name: "Ribbuny", role: "Neutral Support", element: "Neutral",
    location: "Ribbuny spawns in the Sakurajima cherry-blossom fields around (485,-100) to (520,-60). Common daytime spawn near the fast-travel statue north of Sakurajima Castle.",
    desc: "Complete Ribbuny guide — Sakurajima's ribbon-tailed support Pal, best breeding combos, base uses and passives.",
    keywords: "ribbuny guide, palworld ribbuny, how to catch ribbuny, ribbuny breeding, ribbuny sakurajima, ribbuny passives, ribbuny base assignment, palworld ribbuny location, ribbuny spawn, palworld sakurajima pals",
    overview: [
      "Sakurajima DLC Pal introduced with the Feybreak content update.",
      "Handiwork 2 + Gathering 2 — one of the few dual-craft support Pals in the roster.",
      "Partner Skill 'Silk Ribbon' boosts your gathering speed by 15% when Ribbuny leads the party.",
    ],
    breed: [
      "Vixy × Cattiva → Ribbuny. Fastest early combo once Ribbuny is in your Palbox.",
      "Chikipi × Ribbuny → Ribbuny (self-breed) for passive stacking.",
      "Foxparks × Bushi → Ribbuny (patch 1.0 combo).",
    ],
    strategy: [
      "Best placed at Berry Plantation + Mining Site combo for double-duty production.",
      "Reroll passives to Artisan, Serious, Lucky, Work Slave.",
      "Pair with Lyleen for a Sakurajima farming base — see /guides/farming.",
    ],
  },
  {
    slug: "blazehowl", name: "Blazehowl", role: "Fire Mount", element: "Fire",
    location: "Blazehowl spawns across Mount Obsidian's southern rim (330,-460) to (370,-420) at night. Alpha Blazehowl is a mid-game bounty target at (355,-445).",
    desc: "Full Blazehowl guide — fastest catch, best breeding path, saddle stats and passives for a top mid-game Fire mount.",
    keywords: "blazehowl guide, palworld blazehowl, blazehowl noct, how to catch blazehowl, blazehowl breeding, blazehowl saddle, blazehowl mount, palworld fire mount, blazehowl location, blazehowl spawn",
    overview: [
      "Element: Fire. Base stats level 45: HP 3,000 / ATK 130 / DEF 100.",
      "Ground mount with Kindling 3 — great dual base + travel Pal from mid-game onward.",
      "Noct variant swaps Fire for Dark and adds night speed boost.",
    ],
    breed: [
      "Foxparks × Direhowl → Blazehowl. Cheapest 1-cake combo.",
      "Wixen × Rushoar → Blazehowl (alt route).",
      "Blazehowl × Katress → Blazehowl Noct — see /guides/breeding for probabilities.",
    ],
    strategy: [
      "Saddle at level 28 (Tech 29). Fire Blast attack clears Grass-type Alpha camps.",
      "Assign to Furnace when off-duty for Ingot smelting.",
      "Ideal passives: Legend, Ferocious, Runner, Nimble.",
    ],
  },
  {
    slug: "mammorest", name: "Mammorest", role: "Grass Tank Mount", element: "Grass",
    location: "Mammorest wanders the Bamboo Groves (140,-90) to (200,-40). Mammorest Cryst spawns in the Ice Wind Island region at (-170,-500).",
    desc: "Complete Mammorest guide — base tank stats, breeding, best Grass-team pairings and 1.0 raid usage.",
    keywords: "mammorest guide, palworld mammorest, how to catch mammorest, mammorest cryst, mammorest breeding, mammorest saddle, mammorest raid, best mammorest passives, mammorest location, mammorest spawn, palworld grass tank",
    overview: [
      "Element: Grass. Base stats level 50: HP 7,200 / ATK 120 / DEF 95 — one of the highest-HP mounts.",
      "Lumbering 3 makes Mammorest a top wood-farming Pal at any base.",
      "Cryst variant swaps Grass for Ice and gains freeze resistance.",
    ],
    breed: [
      "Bushi × Broncherry → Mammorest. Reliable at 2 cakes.",
      "Wumpo × Dinossom → Mammorest Cryst.",
      "Mammorest × Frostallion → Mammorest Cryst (Ice Wind reroll target).",
    ],
    strategy: [
      "Ground mount with unlimited stamina in forest biomes — best log-hauling mount in 1.0.",
      "Passives: Legend, Vanguard, Stronghold Strategist, Burly Body.",
      "Pair with Petallia at base for medicine + wood pipeline — see /guides/medicine.",
    ],
  },
  {
    slug: "hoocrates", name: "Hoocrates", role: "Dark Support", element: "Dark",
    location: "Hoocrates roosts in Twilight Dunes ruins (260,-360) and around the Anubis boss arena. Alpha Hoocrates spawns near the Ancient Ritual Site.",
    desc: "Complete Hoocrates guide — Dark-element scholar Pal, best breeding combos, base uses and passives.",
    keywords: "hoocrates guide, palworld hoocrates, how to catch hoocrates, hoocrates breeding, hoocrates passives, hoocrates base, palworld dark pal, hoocrates location, hoocrates spawn, hoocrates katress",
    overview: [
      "Element: Dark. Base stats level 30: HP 1,200 / ATK 65 / DEF 70.",
      "Handiwork 2 + Lumbering 1 — early Handiwork upgrade over Lamball.",
      "Partner Skill 'Wisdom of the Owl' boosts base Pal work speed by 10% at night.",
    ],
    breed: [
      "Depresso × Fuack → Hoocrates. Cheapest 1-cake combo.",
      "Katress × Nox → Hoocrates.",
      "Hoocrates × Hoocrates for passive stacking (Serious + Artisan).",
    ],
    strategy: [
      "Assign to Assembly Line Mk II for +10% night crafting speed.",
      "Passives: Serious, Artisan, Lucky, Workaholic.",
      "Not a mount — swap for Blazehowl or Direhowl in combat rotation.",
    ],
  },
  {
    slug: "broncherry", name: "Broncherry", role: "Grass Mount", element: "Grass",
    location: "Broncherry roams the Investigator's Fork forest region (75,-460) to (140,-410). Aqua variant swims near the Sea Breeze Archipelago (250,320).",
    desc: "Full Broncherry guide — Grass ground mount, breeding, base uses and Aqua sub-variant strategy.",
    keywords: "broncherry guide, palworld broncherry, broncherry aqua, how to catch broncherry, broncherry breeding, broncherry saddle, broncherry mount, best broncherry passives, broncherry location, broncherry spawn",
    overview: [
      "Element: Grass. Base stats level 30: HP 2,400 / ATK 100 / DEF 80.",
      "Planting 2 + Lumbering 2 — dual base worker for farming pipelines.",
      "Aqua variant hybrids Grass/Water and can swim across sea gaps.",
    ],
    breed: [
      "Dinossom × Fuack → Broncherry.",
      "Bushi × Mozzarina → Broncherry.",
      "Broncherry × Surfent → Broncherry Aqua.",
    ],
    strategy: [
      "Great early ground mount before you get Direhowl or Blazehowl.",
      "Assign to Berry Plantation for a self-managed Grass base — see /guides/farming.",
      "Passives: Serious, Artisan, Runner, Nimble.",
    ],
  },
  {
    slug: "dinossom", name: "Dinossom", role: "Grass/Dragon Hybrid", element: "Grass / Dragon",
    location: "Dinossom spawns in the Bamboo Groves (155,-80) and Verdant Brook (85,-380). Lux variant is an Electric hybrid in the Fisherman's Point marshlands.",
    desc: "Complete Dinossom guide — best breeding combo, saddle stats, base assignments and Lux variant strategy.",
    keywords: "dinossom guide, palworld dinossom, dinossom lux, how to catch dinossom, dinossom breeding, dinossom saddle, best dinossom passives, dinossom location, dinossom spawn, palworld dragon grass pal",
    overview: [
      "Element: Grass with Dragon sub-typing — hits Dragon damage.",
      "Base stats level 35: HP 2,700 / ATK 110 / DEF 90.",
      "Planting 2 + Handiwork 2 makes it a rare dual-farm and crafting pick.",
    ],
    breed: [
      "Chillet × Direhowl → Dinossom.",
      "Broncherry × Wixen → Dinossom Lux (Electric hybrid).",
      "Dinossom × Grizzbolt → Dinossom Lux — see /breeding.",
    ],
    strategy: [
      "Great mid-game ground mount — Grass + Dragon dual-typing beats most Water-type Alphas.",
      "Assign to Berry Plantation or Handiwork bench when not mounted.",
      "Passives: Legend, Musclehead, Ferocious, Swift.",
    ],
  },
  {
    slug: "cawgnito", name: "Cawgnito", role: "Dark Support", element: "Dark",
    location: "Cawgnito nests in the Twilight Dunes ruins (270,-380) and the northern Land of Absolute Zero cliffs (-125,-440).",
    desc: "Full Cawgnito guide — Dark support Pal, breeding, best base uses and passive setup.",
    keywords: "cawgnito guide, palworld cawgnito, how to catch cawgnito, cawgnito breeding, cawgnito passives, cawgnito base, palworld dark pal, cawgnito location, cawgnito spawn",
    overview: [
      "Element: Dark. Base stats level 18: HP 900 / ATK 55 / DEF 55.",
      "Transporting 1 + Handiwork 1 — early Handiwork stand-in before Vanwyrm.",
      "Partner Skill 'Escape Artist' lets you teleport 20m every 60s while mounted.",
    ],
    breed: [
      "Depresso × Fuack → Cawgnito.",
      "Hoocrates × Nox → Cawgnito.",
      "Cawgnito × Cawgnito for passive stacking.",
    ],
    strategy: [
      "Early Handiwork upgrade — use until you unlock Vanwyrm.",
      "Passives: Artisan, Serious, Workaholic, Lucky.",
      "Not a combat pick — swap for Direhowl or Blazehowl for mid-game combat.",
    ],
  },
  {
    slug: "leezpunk", name: "Leezpunk", role: "Dark/Dragon Rogue", element: "Dark / Dragon",
    location: "Leezpunk spawns across the Twilight Dunes and Bounty Hunter Ridge (280,-360). Ignis variant appears in Mount Obsidian at night.",
    desc: "Full Leezpunk guide — Dark rogue Pal, breeding, saddle stats, Ignis variant and best passives.",
    keywords: "leezpunk guide, palworld leezpunk, leezpunk ignis, how to catch leezpunk, leezpunk breeding, leezpunk saddle, best leezpunk passives, leezpunk location, leezpunk spawn",
    overview: [
      "Element: Dark with Dragon sub-typing.",
      "Base stats level 28: HP 1,400 / ATK 100 / DEF 60.",
      "Handiwork 2 + Mining 2 — great early crafting and mining hybrid.",
    ],
    breed: [
      "Direhowl × Nox → Leezpunk.",
      "Leezpunk × Foxparks → Leezpunk Ignis (Fire hybrid).",
      "Leezpunk × Leezpunk for passive stacking.",
    ],
    strategy: [
      "Ground mount with high mobility and low HP — glass-cannon early-game pick.",
      "Assign to Mining Site off-duty for extra Stone/Ore.",
      "Passives: Legend, Runner, Swift, Ferocious.",
    ],
  },
  {
    slug: "incineram", name: "Incineram", role: "Fire/Dark Mount", element: "Fire / Dark",
    location: "Incineram roams Mount Obsidian and Sealed Realm of the Guardian (340,-475). Noct variant is Dark-only, spawning near Anubis' arena.",
    desc: "Full Incineram guide — Fire/Dark hybrid mount, breeding, saddle stats and passives.",
    keywords: "incineram guide, palworld incineram, incineram noct, how to catch incineram, incineram breeding, incineram saddle, best incineram passives, incineram location, incineram spawn",
    overview: [
      "Element: Fire with Dark sub-typing (Noct variant is pure Dark).",
      "Base stats level 33: HP 2,700 / ATK 115 / DEF 90.",
      "Kindling 2 + Handiwork 3 — top mid-game crafter and campfire worker.",
    ],
    breed: [
      "Foxparks × Rushoar → Incineram.",
      "Blazehowl × Nox → Incineram Noct.",
      "Incineram × Incineram for passive stacking.",
    ],
    strategy: [
      "Ground mount with Fire Blast — great vs Grass Alphas.",
      "Assign to Assembly Line for +15% craft speed.",
      "Passives: Legend, Artisan, Ferocious, Musclehead.",
    ],
  },
  {
    slug: "rushoar", name: "Rushoar", role: "Ground Rusher", element: "Ground",
    location: "Rushoar spawns across the Rayne Syndicate Tower Entrance region and the Windswept Hills (55,-490) to (95,-450). Alpha Rushoar is a bounty at (75,-475).",
    desc: "Complete Rushoar guide — Ground charger Pal, breeding, base uses and best passives.",
    keywords: "rushoar guide, palworld rushoar, how to catch rushoar, rushoar breeding, rushoar saddle, best rushoar passives, rushoar location, rushoar spawn, palworld ground pal",
    overview: [
      "Element: Ground. Base stats level 12: HP 900 / ATK 80 / DEF 60.",
      "Mining 2 makes Rushoar a top early Stone/Ore farmer.",
      "Partner Skill 'Charge' does 200% damage vs Electric-type Pals.",
    ],
    breed: [
      "Foxparks × Chikipi → Rushoar.",
      "Cattiva × Direhowl → Rushoar.",
      "Rushoar × Rushoar for passive stacking.",
    ],
    strategy: [
      "First real ground mount — usable from level 10 onward.",
      "Assign to Mining Site or Stone Pit for early automation.",
      "Passives: Runner, Nimble, Musclehead, Ferocious.",
    ],
  },
  {
    slug: "bastigor", name: "Bastigor", role: "Ice Bruiser", element: "Ice",
    location: "Bastigor roams the World Tree region. Players also report hatching it from a Huge Frozen Egg found near the tower, which is the most reliable way to get one.",
    desc: "Full Bastigor guide — Ice element Feybreak Pal, real stats, breeding combo, work suitability and drops.",
    keywords: "bastigor guide, palworld bastigor, bastigor element, how to catch bastigor, bastigor breeding, bastigor stats, bastigor world tree, bastigor huge frozen egg, bastigor location, bastigor spawn",
    overview: [
      "Element: Ice. Base growth: HP 140 / ATK 130 / DEF 120, reaching roughly 6,500–8,180 HP and 880–1,114 attack at level 80.",
      "Work suitability: Lumbering 3, Mining 3, Cooling 4 — one of the few Pals that covers heavy industry and refrigeration at once.",
      "Rideable, hostile on sight, and introduced with the Feybreak trailer. Takes 1.5× damage from Fire and only 0.66× from Ice and Dragon.",
      "Drops Ice Organ and Leather. Learns Stone Cannon (Lv1), Iceberg (Lv7) and Rockburst (Lv30).",
    ],
    breed: [
      "Frostallion × Anubis → Bastigor — the confirmed breeding combo.",
      "Bastigor × Bastigor for passive stacking once you have a pair.",
      "Incubate the resulting egg in a Huge Frozen Egg incubator setup for the fastest cycle.",
    ],
    strategy: [
      "Bipedal liger-like Pal with light-green leonine fur; anything round triggers an uncontrollable urge to punch it.",
      "Assign to a Cooling-heavy base: Cooling 4 keeps refrigerators and cake ingredients stocked while Mining 3 and Lumbering 3 feed the workbenches.",
      "Bring Fire resistance when fighting it — Bastigor takes 1.5× Fire damage but resists Ice and Dragon.",
    ],
  },

  {
    slug: "panthalus", name: "Panthalus", role: "Water Mount", element: "Water",
    location: "Panthalus roams the Feybreak coast and Sea Breeze Archipelago (280,320) to (340,380). Alpha spawns at (310,350) after storms.",
    desc: "Full Panthalus guide — Feybreak Water mount, breeding, saddle stats and best team fit.",
    keywords: "panthalus guide, palworld panthalus, how to catch panthalus, panthalus breeding, panthalus saddle, panthalus mount, best panthalus passives, panthalus feybreak, panthalus location, panthalus spawn",
    overview: [
      "Element: Water. Base stats level 40: HP 3,800 / ATK 125 / DEF 100.",
      "Water swimming mount — faster than Surfent Terra by 20%.",
      "Watering 3 makes it a top Berry Plantation Pal off-duty.",
    ],
    breed: [
      "Jormuntide × Suzaku Aqua → Panthalus.",
      "Azurobe × Broncherry Aqua → Panthalus.",
      "Panthalus × Panthalus for passive stacking.",
    ],
    strategy: [
      "Water mount + Watering 3 = best Sakurajima/Feybreak farming pick.",
      "Passives: Legend, Swift, Runner, Nimble.",
      "See /mounts for Feybreak swim-mount comparison.",
    ],
  },
  {
    slug: "hartalis", name: "Hartalis", role: "Neutral Support", element: "Neutral",
    location: "Hartalis grazes the Feybreak meadows (270,-320) to (310,-270). Alpha Hartalis is a bounty target near the Feybreak trading post.",
    desc: "Complete Hartalis guide — Feybreak support Pal, breeding, base uses and passive setup.",
    keywords: "hartalis guide, palworld hartalis, how to catch hartalis, hartalis breeding, hartalis passives, hartalis base, hartalis feybreak, hartalis location, hartalis spawn",
    overview: [
      "Element: Neutral. Base stats level 30: HP 2,200 / ATK 90 / DEF 80.",
      "Handiwork 2 + Planting 2 + Gathering 2 — triple-role support Pal.",
      "Partner Skill boosts party heal rate by 10% while equipped.",
    ],
    breed: [
      "Mossanda × Petallia → Hartalis.",
      "Lyleen × Ribbuny → Hartalis.",
      "Hartalis × Hartalis for passive stacking.",
    ],
    strategy: [
      "Best jack-of-all-trades base Pal — assign to Berry Plantation for triple output.",
      "Passives: Artisan, Serious, Workaholic, Lucky.",
      "Pair with Lyleen for medicine crafting — see /guides/medicine.",
    ],
  },
  {
    slug: "dandilord", name: "Dandilord", role: "Grass Boss", element: "Grass",
    location: "Dandilord is a Sakurajima field boss at (490,-70). Spawns every real-time hour on daily reset days.",
    desc: "Full Dandilord guide — Sakurajima Grass field boss, catch strategy, breeding and best passives.",
    keywords: "dandilord guide, palworld dandilord, how to catch dandilord, dandilord breeding, dandilord passives, dandilord boss, dandilord sakurajima, dandilord location, dandilord spawn",
    overview: [
      "Element: Grass. Base stats level 44: HP 5,600 / ATK 135 / DEF 105 — a Sakurajima field boss.",
      "Planting 3 + Gathering 3 — top Sakurajima farming Pal.",
      "Partner Skill 'Bloom' spawns a healing seed cluster every 45s when active.",
    ],
    breed: [
      "Lyleen × Mossanda → Dandilord.",
      "Petallia × Broncherry → Dandilord.",
      "Dandilord × Dandilord for passive stacking.",
    ],
    strategy: [
      "Catch at Mercy Legendary Sphere — Alpha version drops Ancient Tech Points ×2.",
      "Assign to Berry Plantation + Ranch for double output.",
      "Passives: Legend, Artisan, Serious, Lucky.",
    ],
  },
  {
    slug: "knocklem", name: "Knocklem", role: "Ground Bruiser", element: "Ground",
    location: "Knocklem stomps the Feybreak canyons (315,-350) to (360,-300). Alpha Knocklem spawns at (335,-330) after in-game earthquakes.",
    desc: "Full Knocklem guide — Feybreak Ground bruiser, breeding, saddle stats and best raid loadout.",
    keywords: "knocklem guide, palworld knocklem, how to catch knocklem, knocklem breeding, knocklem saddle, knocklem passives, knocklem feybreak, knocklem location, knocklem spawn",
    overview: [
      "Element: Ground. Base stats level 42: HP 5,000 / ATK 145 / DEF 110.",
      "Mining 3 + Lumbering 3 — top-tier heavy-industry base Pal.",
      "Partner Skill 'Seismic Slam' deals AoE Ground damage while mounted.",
    ],
    breed: [
      "Digtoise × Reptyro → Knocklem.",
      "Anubis × Bastigor → Knocklem.",
      "Knocklem × Knocklem for passive stacking.",
    ],
    strategy: [
      "Best raid mount for the Bellanoir raid — Seismic Slam interrupts phase 2 cast.",
      "Assign to Ore Mining Site + Coal Mine for a self-running mining loop.",
      "Passives: Legend, Musclehead, Ferocious, Vanguard.",
    ],
  },
  {
    slug: "flaracle", name: "Flaracle", role: "Fire Seer", element: "Fire",
    location: "Flaracle appears in the World Tree region. High-level Alpha and Boss encounters are the best source of its rare relic drops.",
    desc: "Complete Flaracle guide — Fire element Pal, Palpedia lore, Alpha and Boss drop table and farming strategy.",
    keywords: "flaracle guide, palworld flaracle, flaracle element, how to catch flaracle, flaracle drops, flaracle alpha, flaracle fire radiant gem, flaracle location, flaracle spawn, palworld fire pal",
    overview: [
      "Element: Fire. A single-element Pal that focuses its mind and reads the future through flames.",
      "Palpedia lore: if it glimpses an inconvenient destiny it relentlessly rerolls the prophecy until the result is favourable — so the future of Flaracle is always exceptionally bright.",
      "Kindling work suitability makes it a straightforward Furnace and forge worker once captured.",
    ],
    breed: [
      "Fire × Fire pairings in the World Tree tier are the practical route; Pocketpair has not published a unique combo for Flaracle.",
      "Flaracle × Flaracle once you own a pair, to stack passives.",
      "Cake supply matters more than the parents here — keep a Cooling Pal on the refrigerator.",
    ],
    strategy: [
      "Farm level 70 Alpha or Boss Flaracle for the good drops: Leather ×3–4 and Flame Organ ×3–4 are guaranteed, World Tree Holy Water ×1–3 and Fire Radiant Gem ×1–3 both land at 50%.",
      "Ancient Relics are the chase: Decayed ×1–10 at 10%, Dormant ×1–5 at 5%, Gorgeous ×1–3 at 3.33%, Glowing ×1–2 at 2.5% and Glistening ×1 at 2%.",
      "Bring Water-element attackers — Fire Pals take extra damage from Water, which shortens the Alpha fight and the relic grind.",
    ],
  },

  {
    slug: "reptyro", name: "Reptyro", role: "Fire/Ground Tank", element: "Fire / Ground",
    location: "Reptyro roams Mount Obsidian's central plateau (340,-475). Cryst variant spawns in the Land of Absolute Zero as a Fire/Ice hybrid.",
    desc: "Full Reptyro guide — Fire/Ground tank Pal, breeding, saddle stats and Cryst sub-variant strategy.",
    keywords: "reptyro guide, palworld reptyro, reptyro cryst, how to catch reptyro, reptyro breeding, reptyro saddle, best reptyro passives, reptyro location, reptyro spawn, palworld fire ground pal",
    overview: [
      "Element: Fire with Ground sub-typing.",
      "Base stats level 42: HP 6,800 / ATK 140 / DEF 130 — a top-tier tank mount.",
      "Kindling 4 + Mining 2 makes Reptyro the best Furnace + Mining hybrid.",
    ],
    breed: [
      "Bushi × Blazehowl → Reptyro.",
      "Anubis × Wumpo → Reptyro.",
      "Reptyro × Frostallion → Reptyro Cryst.",
    ],
    strategy: [
      "Ground mount with lava-immunity — traverses Mount Obsidian without heat damage.",
      "Assign to Improved Furnace for +30% smelting speed.",
      "Passives: Legend, Musclehead, Ferocious, Stronghold Strategist. See /mounts for tank rankings.",
    ],
  },
  {
    slug: "grizzbolt", name: "Grizzbolt", role: "Electric Bruiser", element: "Electric",
    location: "Grizzbolt roams the Bamboo Groves and Verdant Brook region around coords (-115, -95) to (-70, -40). Alpha Grizzbolt spawns nightly at (-92, -68) around level 26.",
    desc: "Complete Grizzbolt guide for Palworld 1.0 — catch route, breeding combos, minigun saddle, best passives and TD01 TCG deck cover strategy.",
    keywords: "grizzbolt guide, palworld grizzbolt, how to catch grizzbolt, grizzbolt breeding combo, grizzbolt saddle, grizzbolt minigun, best grizzbolt passives, grizzbolt location, grizzbolt spawn, grizzbolt tcg, grizzbolt td01, palworld electric pal, grizzbolt vs relaxaurus lux",
    overview: [
      "Element: Electric — 125% damage vs Water Pals like Jormuntide and Azurobe, weak to Ground.",
      "Base stats level 30: HP 4,300 / ATK 120 / DEF 100. Signature Pal for the Palworld TCG TD01 trial deck cover.",
      "Handiwork 3 + Lumbering 2 + Generating Electricity 2 — one of the few Pals that powers Electric Generators AND crafts at base.",
    ],
    breed: [
      "Mossanda × Rayhound → Grizzbolt. Standard combo at ~5 cakes.",
      "Univolt × Broncherry → Grizzbolt. Cheaper mid-game path.",
      "Grizzbolt × Grizzbolt → Grizzbolt. Best for stacking Lord of Lightning + Ferocious passives.",
    ],
    strategy: [
      "Saddle unlocks a shoulder-mounted minigun — see /guides/weapons for ammo economy tips.",
      "Assign to Electric Generator (Tech 26) to keep late-game base lit without coal.",
      "Ideal passives: Lord of Lightning, Ferocious, Musclehead, Legend. Reroll at /breeding.",
      "In TCG (/cards), Grizzbolt is the TD01 headline — pair with Foxparks for a Grass/Fire/Electric splash. See /guides/palworld-trial-deck.",
    ],
  },
  {
    slug: "chillet", name: "Chillet", role: "Dragon/Ice Support", element: "Dragon / Ice",
    location: "Chillet is found in the Bridge of the Twin Knights area and the northern grasslands around coords (85, -520) to (140, -470). Alpha Chillet spawns at (112, -488) around level 22.",
    desc: "Full Chillet guide for Palworld 1.0 — catch route, breeding paths, mount speed, best passives and Dragon/Ice combat strategy.",
    keywords: "chillet guide, palworld chillet, how to catch chillet, chillet breeding, chillet mount, chillet saddle, best chillet passives, chillet location, chillet spawn, chillet vs eikthyrdeer, palworld dragon ice pal, chillet ignis",
    overview: [
      "Element: Dragon with Ice sub-typing — flexible coverage vs Dragon and Ground enemies.",
      "Base stats level 25: HP 2,900 / ATK 105 / DEF 80. Fast ground mount with a low stamina drain curve.",
      "Cooling 2 + Gathering 2 — powers Cooler Boxes and doubles as a Berry Plantation harvester.",
    ],
    breed: [
      "Fuack × Eikthyrdeer → Chillet. Cheap early-game combo at ~2 cakes.",
      "Celaray × Direhowl → Chillet. Alternative using Verdant Brook parents.",
      "Chillet × Chillet → Chillet Ignis (event variant). Ideal passive stacking target.",
    ],
    strategy: [
      "Ground mount speed 700 with saddle — a smoother early alternative to Eikthyrdeer for the Small Settlement run.",
      "Assign to Cooler Box for stable food storage; see /guides/ore-coal-farming-bases for the cold-chain layout.",
      "Ideal passives: Runner, Nimble, Swift, Legend. Reroll with cakes at /breeding.",
      "Pair Chillet with Foxparks or Rooby for elemental coverage against early bosses — see /guides/bosses.",
    ],
  },
];

for (const p of newPals) GUIDES_MORE.push(palGuide(p));

// Best base locations for ore and coal farming
GUIDES_MORE.push({
  slug: "ore-coal-farming-bases",
  title: "Best Base Locations for Ore & Coal Farming — Palworld",
  desc: "The best Palworld base coordinates for ore and coal farming, with Pal loadouts, defense tips and yield-per-hour numbers.",
  tag: "Base Building",
  updated: "2026-07-28",
  keywords:
    "palworld ore farming, palworld coal farming, best ore base palworld, best coal base palworld, palworld ore locations, palworld coal locations, palworld mining base, palworld ore coal base coordinates, palworld digtoise base, palworld mining pals, palworld base mining setup, palworld infinite ore, palworld infinite coal, palworld ore respawn, palworld coal respawn",
  articles: [
    {
      title: "Best ore farming base locations in Palworld",
      slug: "best-ore-bases",
      intro:
        "Ore is the backbone of every Palworld weapon and refined ingot recipe. The three coordinates below place your base directly on top of ore-node clusters that respawn on server reset, so a Digtoise + Anubis loop can auto-farm 1,000+ ore per real-time hour.",
      sections: [
        { heading: "Ore Cluster 1 — Deep Sand Dunes (350, 350)", text: "Six ore nodes in a tight 25m radius. Flat ground makes it the easiest ore base to defend. Bring Foxparks or Rooby to counter the desert heat while your Miners work." },
        { heading: "Ore Cluster 2 — Verdant Brook ridge (-150, -80)", text: "Five ore nodes plus two stone nodes on a shelf above the Small Settlement. Temperate biome — no cold or heat gear needed. Ideal first mining base." },
        { heading: "Ore Cluster 3 — Bamboo Groves plateau (75, -430)", text: "Seven ore nodes wrapped in a natural cliff wall. Highest node count on the map; pair with a Level 3 Palbox for a 15-slot mining crew." },
        { heading: "Recommended mining Pals", text: "Digtoise (Mining 3) is the fastest per-swing miner. Anubis (Mining 3 + Handiwork 4) doubles as a crafter. Tombat (Mining 2) works overnight. Assign 2 Digtoise + 1 Anubis + 1 Cattiva for the hauling loop." },
        { heading: "Yield per hour", text: "A 3-Digtoise loadout on Cluster 3 with a Level 3 Palbox produces roughly 1,050 ore and 400 stone per real-time hour once nodes fully respawn." },
      ],
      externalLinks: pool(L.fandom, L.gamerant, L.pcgamer),
    },
    {
      title: "Best coal farming base locations in Palworld",
      slug: "best-coal-bases",
      intro:
        "Coal fuels every Refined Ingot, Carbon Fiber and Legendary weapon recipe in Palworld 1.0. These three coordinates sit on the densest coal-node clusters in the game and are the fastest way to stockpile coal for endgame gear.",
      sections: [
        { heading: "Coal Cluster 1 — Mount Obsidian west face (-260, -510)", text: "Five coal nodes in a heat-safe pocket just above the lava rivers. Best late-game coal base once you have Heat-Resistant Pal Metal Armor." },
        { heading: "Coal Cluster 2 — Volcano north crater (200, -540)", text: "Four coal nodes plus two ore nodes. Slightly harder to defend from Alpha Blazamut patrols but doubles as an ore side-farm." },
        { heading: "Coal Cluster 3 — Sealed Realm of the Swordmaster ridge (-350, -450)", text: "Three coal nodes with the highest node-density-per-Palbox-radius on the map. Cold biome — bring Foxparks or Arsox for warmth." },
        { heading: "Recommended coal Pals", text: "Digtoise stays the top swing-per-second miner. Astegon (Mining 4) is the endgame king — best coal Pal in the game, but requires level 47. Blazamut (Mining 3 + Kindling 4) doubles for the Furnace loop right next to the base." },
        { heading: "Yield per hour", text: "A 2-Astegon + 1-Digtoise loadout on Cluster 1 clears the five coal nodes every 6 minutes, netting roughly 250 coal per real-time hour." },
      ],
      externalLinks: pool(L.gamerant, L.ign, L.reddit),
    },
    {
      title: "Ore & coal base defense, loadout and Palbox tips",
      slug: "mining-base-defense",
      intro:
        "A great mining coordinate is worthless if raids wipe your Digtoise mid-shift. This walkthrough covers defense turrets, Pal loadouts and Palbox layouts that keep an ore or coal base running through a full 8-hour offline session.",
      sections: [
        { heading: "Palbox and worker slots", text: "Upgrade the Palbox to Level 3 for 20 worker slots before you commit to a mining base. Assign 4 miners, 2 haulers (Cattiva or Lifmunk), 1 kindler for the on-site Furnace and 1 medic (Wumpo Botan or Lyleen)." },
        { heading: "Defense turrets and walls", text: "Two Musket Turrets on the ridge side and one Missile Turret at the entrance stop every scripted raid up to level 45. Wrap the base in Level 3 Metal Walls — Alpha Anubis and Alpha Blazamut cannot break them." },
        { heading: "Food and sanity loop", text: "Two Cooler Boxes stocked with cooked Chikipi + Berry Plantation output keeps sanity above 60. Add one Hot Spring so your miners never trigger a work-strike." },
        { heading: "Passive skills to reroll", text: "Legend, Artisan, Serious and Work Slave stack multiplicatively for +130% mining speed. Reroll at /breeding using cakes farmed from your Ranch." },
        { heading: "FAQ, checklist and links", text: "See /guides/farming for the Pal Metal Ingot loop, /guides/ore-coal-farming-bases for wall layouts, and /breeding for the Digtoise + Anubis passive stack." },
      ],
      externalLinks: pool(L.pcgamer, L.polygon, L.reddit),
    },
    {
      title: "Ore & coal farming FAQ — Palworld",
      slug: "ore-coal-faq",
      intro: "Quick answers to the most-asked Palworld ore and coal farming questions, sourced from patch 1.0 in-game testing.",
      sections: [
        { heading: "What is the best base for ore farming in Palworld?", text: "The Deep Sand Dunes coordinate (350, 350) is the best all-round ore farming base in Palworld — six ore nodes, flat defensible ground, and no cold-weather gear required for your miners." },
        { heading: "What is the best base for coal farming in Palworld?", text: "Mount Obsidian's west face at (-260, -510) is the best coal farming base in Palworld. Five coal nodes sit inside a single Palbox radius, and the ridge blocks most raid spawns." },
        { heading: "Who is the best mining Pal in Palworld?", text: "Astegon is the best mining Pal in Palworld at Mining 4, followed by Anubis and Digtoise at Mining 3. Digtoise is the fastest per-swing miner and unlocks earliest via breeding." },
        { heading: "How long does ore respawn in Palworld?", text: "Ore nodes respawn roughly every 60 real-time minutes on default multiplayer settings, and immediately when you sleep to skip the night on single-player." },
        { heading: "Can you get coal without mining it?", text: "Yes — Digtoise, Astegon and Blazamut can drop coal chunks as loot, and Black Marketeers occasionally stock 20 coal for 300 gold. Mining a dedicated coal base is still 5–10x faster." },
      ],
      externalLinks: pool(L.fandom, L.gamerant, L.dexerto),
    },
  ],
});

GUIDES_MORE.push({
  slug: "breeding-mechanics",
  title: "Palworld Breeding Guide — Breed Power, Cakes & Ranch Optimization",
  desc: "The definitive Palworld breeding guide: how breed power works, cake production loops, ranch optimization, passive skill inheritance and legendary breeding routes.",
  tag: "Breeding",
  updated: "2026-07-28",
  keywords:
    "palworld breed power, palworld breeding mechanics, palworld ranch breeding, palworld cake recipe, palworld ranch optimization, palworld breeding calculator, palworld passive skill inheritance, how to breed pals palworld, palworld breeding farm setup, palworld egg hatching, palworld best breeding combos, palworld jetragon breeding, palworld anubis breeding, palworld legendary breeding, palworld breed power chart, palworld cake production, palworld breeding cake",
  articles: [
    {
      title: "How Palworld breed power works — the math behind every egg",
      slug: "breed-power-explained",
      intro:
        "Palworld breeding is not random. Every Pal has a hidden Breed Power (also called Combi-Rank) value between roughly 10 and 1500, and the child that pops out of the egg is decided by the exact formula (parentA_power + parentB_power + 1) ÷ 2, rounded down. Learn the math and you can breed any Pal in the game on the first try.",
      sections: [
        { heading: "The Breed Power formula", text: "Child Breed Power = floor((Parent A + Parent B + 1) / 2). Palworld then picks the Pal with the closest Breed Power at or below that number as the child. There are ~130 explicit override pairings for special Pals like Jetragon, Frostallion and Anubis, which always override the math." },
        { heading: "Reading a Breed Power chart", text: "Community-datamined tables list every Pal's Breed Power. Low-tier Pals like Lamball (1500) and Cattiva (1490) sit at the top of the chart, while endgame Pals like Jetragon (100) and Frostallion (110) sit near the bottom. Lower Breed Power = later-game Pal." },
        { heading: "Worked example — breeding Anubis", text: "Anubis (Breed Power 470) is produced by two parents averaging 470. Penking (500) + Bushi (440) = floor((500+440+1)/2) = 470 → Anubis. This is why the classic Penking × Bushi combo is the earliest reliable Anubis route." },
        { heading: "Why some combos are overrides", text: "Jetragon, Frostallion, Necromus, Paladius and Eidrolon use fixed override pairs regardless of Breed Power. Use the Pal Stars breeding calculator at /breeding to see the exact override for every legendary." },
      ],
      externalLinks: pool(L.fandomBreed, L.gamerant, L.reddit),
    },
    {
      title: "Cake production loop — the fastest way to farm breeding cakes",
      slug: "cake-production-loop",
      intro:
        "Every Palworld egg costs one cake. A cake needs 5 Flour, 8 Red Berries, 7 Milk, 8 Eggs and 2 Honey — sourced from Pals, not the map. The optimal cake production loop turns a single base into a self-sustaining breeding factory that outputs 4-6 cakes per real-time hour.",
      sections: [
        { heading: "Cake recipe breakdown", text: "1 Cake = 5 Flour + 8 Red Berries + 7 Milk + 8 Eggs + 2 Honey. Cook it at the Cooking Pot (unlocked at Technology Level 17). Cakes do not spoil in a Cooler Box." },
        { heading: "Base layout — the cake factory", text: "Place a Wheat Plantation (Mozzarina/Broncherry seeding), a Berry Plantation, a Ranch, and a Mill in a tight circle around a single Feed Box. Put the Cooking Pot 5m away so the cook Pal auto-hauls ingredients." },
        { heading: "Ranch Pals that produce cake ingredients", text: "Mozzarina drops Milk. Chikipi drops Eggs. Beegarde drops Honey. Vixy drops nothing useful for cakes but is great for a second Ranch. Assign 1 of each plus a Cattiva or Lifmunk hauler." },
        { heading: "Worker assignments", text: "You need Planting (Petallia, Lyleen), Watering (Pengullet, Fuack), Gathering (Cattiva, Robinquill), Kindling (Foxparks, Blazehowl) and Handiwork (Anubis, Grintale). Anubis + Lyleen carries the entire loop by itself." },
        { heading: "Yield per hour", text: "A Level 3 Palbox with this loadout produces 4–6 cakes per real-time hour, enough to run two Breeding Farms nonstop. Double the Ranch and Wheat Plantation for endgame legendary breeding sessions." },
      ],
      externalLinks: pool(L.pcgamer, L.ign, L.gamerant),
    },
    {
      title: "Ranch optimization and passive skill inheritance",
      slug: "ranch-and-passives",
      intro:
        "The Ranch is the second half of Palworld's breeding economy: it drops the Milk, Eggs and Honey your cakes need, and it doubles as a passive-skill farm. Optimizing your Ranch layout and understanding passive inheritance is what separates a level-30 casual base from a legendary-breeding endgame factory.",
      sections: [
        { heading: "Ranch capacity and Pal assignments", text: "One Ranch supports 4 assigned Pals. Optimal loadout for breeding: 2 Mozzarina (Milk), 1 Chikipi (Eggs), 1 Beegarde (Honey). Add a second Ranch for double throughput or swap in Vixy (Pal Spheres) for a mixed economy." },
        { heading: "How passive skill inheritance works", text: "Each parent has up to 4 passives. The child inherits 1–4 passives, weighted toward the parent with higher-tier passives. Reroll by re-breeding until the child rolls Legend, Lucky, Ferocious and Musclehead — the top DPS stack." },
        { heading: "The 4-passive god-roll", text: "For a DPS Pal target: Legend (+15% ATK/-10% dmg taken), Musclehead (+30% ATK, -50% work speed), Ferocious (+20% ATK) and Lucky (+15% ATK). For a worker Pal target: Artisan, Serious, Work Slave and Legend." },
        { heading: "Gender odds and re-roll speed", text: "Every egg is 50/50 gender. Legendary eggs take 2 hours 30 minutes to hatch on default settings — reduce hatch time to 30 minutes in the world settings for faster passive rolls. Use Incubators near a campfire for the +Heat bonus." },
        { heading: "FAQ, checklist and internal links", text: "See /breeding for the full parent-pair calculator, /guides/farming for the wheat and berry farm loop, and /guides/passive-skills for every passive skill in Palworld 1.0." },
      ],
      externalLinks: pool(L.fandomBreed, L.polygon, L.reddit),
    },
    {
      title: "Palworld breeding FAQ — cakes, breed power and legendaries",
      slug: "breeding-faq",
      intro: "Quick answers to the most-searched Palworld breeding questions, verified against patch 1.0 in-game testing.",
      sections: [
        { heading: "How does Palworld breeding work?", text: "Place one male and one female Pal at a Breeding Farm with one cake in the feed box. After a short timer they produce an egg; the child is determined by the average of both parents' Breed Power values, unless the pair is an override pair for a legendary." },
        { heading: "What is Breed Power in Palworld?", text: "Breed Power (Combi-Rank) is a hidden per-Pal value from ~10 to ~1500. Child Breed Power = floor((Parent A + Parent B + 1) / 2). The game then picks the Pal with the closest matching Breed Power as the offspring." },
        { heading: "How do you make a cake in Palworld?", text: "Cook 5 Flour, 8 Red Berries, 7 Milk, 8 Eggs and 2 Honey at a Cooking Pot. Milk comes from Mozzarina, Eggs from Chikipi and Honey from Beegarde, all assigned to a Ranch. Flour is milled from wheat grown at a Wheat Plantation." },
        { heading: "How do you breed Jetragon in Palworld?", text: "Breed Blazamut × Suzaku, or Anubis × Astegon, at a Breeding Farm with a cake. Both pairings are override combos that always produce a Jetragon egg regardless of Breed Power math." },
        { heading: "How do you breed Anubis in Palworld?", text: "Penking × Bushi is the easiest early Anubis route — both parents average a Breed Power of 470, which is Anubis's exact value. You can also breed Anubis from Mossanda × Rushoar." },
        { heading: "How long do Palworld eggs take to hatch?", text: "Common eggs take 10 minutes, Uncommon 30 minutes, Rare 1 hour, Epic 1 hour 30 minutes and Legendary 2 hours 30 minutes on default settings. Reduce hatch time in world settings for faster passive-skill rerolls." },
        { heading: "Do passive skills transfer when breeding?", text: "Yes. Each child inherits 1–4 passives from its parents, weighted by tier. Stack Legend + Musclehead + Ferocious + Lucky on both parents to maximise the odds of rolling the 4-passive god-roll on the child." },
      ],
      externalLinks: pool(L.fandomBreed, L.dexerto, L.gamerant),
    },
  ],
});






